import axios from "axios";

// Call the API on the SAME ORIGIN that served the page so the Authorization
// header is preserved end-to-end. A cross-origin custom-hostname path can drop
// custom headers at the edge, which caused the production 401 "Not authenticated".
// Falls back to REACT_APP_BACKEND_URL for any non-browser context.
const ORIGIN = (typeof window !== "undefined" && window.location && window.location.origin) || "";
const BASE = ORIGIN || process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BASE}/api`;

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("suite_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Demo mode: block file downloads / exports (blob responses) so demos can't print/export data.
  // Manuals/changelogs stay allowed (they're product documentation, not user data).
  if (localStorage.getItem("suite_demo") === "1" && config.responseType === "blob"
      && !String(config.url || "").includes("/manual")) {
    return Promise.reject({ __demoBlocked: true, message: "Printing and exporting are disabled in demo mode." });
  }
  return config;
});

// Endpoints where a 401 is an expected "wrong credentials/code" response — never force sign-out on these.
const AUTH_ENTRY = [
  "/auth/login", "/auth/mfa/", "/auth/support-login", "/auth/support-mfa/",
  "/auth/register", "/auth/recover/", "/auth/setup-password", "/auth/contact-change",
];

// If an authenticated call is rejected (token expired or session revoked server-side, e.g. the
// 15-min idle sign-out or an admin ending sessions), clean up and return to /login instead of
// leaving pages stuck on "Loading…".
client.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const hadToken = !!localStorage.getItem("suite_token");
    const isEntry = AUTH_ENTRY.some((p) => url.includes(p));
    // Only force sign-out when the backend explicitly says the SESSION is over
    // (expired / invalid / revoked / user gone). A bare "Not authenticated" can be a
    // transient header drop at the edge — do NOT wipe the session on that, or a single
    // hiccup cascades into a full logout and hides the real cause.
    const detail = error?.response?.data?.detail;
    const detailStr = typeof detail === "string" ? detail.toLowerCase() : "";
    const sessionEnded = /expired|invalid token|invalid signature|user not found|revoked|session/.test(detailStr);
    if (status === 401 && hadToken && !isEntry && sessionEnded) {
      localStorage.removeItem("suite_token");
      localStorage.removeItem("suite_support_session");
      sessionStorage.removeItem("suite_caps");
      if (!window.location.pathname.startsWith("/login")) {
        window.location.assign("/login?expired=1");
      }
    }
    return Promise.reject(error);
  }
);

export function apiErr(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(" ");
  return e?.message || "Something went wrong";
}

export default client;
